import { ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button/Button'
import { Tooltip } from 'core-components/library/tooltip'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import {
  buildEltDocumentOpenLink,
  buildQbrDocumentOpenLink,
  canDownloadEltPptx,
  canDownloadQbrPptx,
  getEltDocumentDisplayName,
  getQbrDocumentDisplayName,
  IPartnerDocumentSection,
} from '../../../../lib/documentsAndLinks'
import {
  downloadEltDocumentPptx,
  downloadQbrDocumentPptx,
} from '../../../../lib/documentsAndLinks/api'
import { CockpitExpandButton } from '../../../components/cockpitExpandButton'
import { CockpitSectionHeader } from '../../../components/cockpitSectionHeader'
import { useCockpitEltCreationPeriod } from '../../hooks/useCockpitEltCreationPeriod'
import { CockpitDocumentLinks } from '../cockpitDocumentLinks/CockpitDocumentLinks'
import { EltCreateControls } from '../eltCreateControls/EltCreateControls'
import { QbrCreateControls } from '../qbrCreateControls/QbrCreateControls'

import styles from './CockpitDocumentsCollapsedSummary.module.scss'

interface ICockpitDocumentsCollapsedSummaryProps {
  exportPlan?: ReactNode
  sections: IPartnerDocumentSection[]
  canCreate: boolean
  showLinks: boolean
  onExpand?: () => void
}

const COLLAPSED_PARTNER_CAP = 4
const COLLAPSED_DOCUMENTS_PER_TYPE_CAP = 2
const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks

export const CockpitDocumentsCollapsedSummary = ({
  sections,
  canCreate,
  showLinks,
  onExpand,
  exportPlan,
}: ICockpitDocumentsCollapsedSummaryProps) => {
  const { fYear, fQuarter } = useCockpitEltCreationPeriod()
  const hasDocuments = sections.some(
    (section) => section.eltDocuments.length > 0 || section.qbrDocuments.length > 0
  )

  if (sections.length === 0 || (!canCreate && !showLinks && !hasDocuments)) {
    return (
      <div className={styles.empty}>
        <Translation span text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks.emptyElt} />
      </div>
    )
  }

  if (sections.length <= 1) {
    const section = sections[0]
    const visibleEltDocuments = section.eltDocuments.slice(0, COLLAPSED_DOCUMENTS_PER_TYPE_CAP)
    const visibleQbrDocuments = section.qbrDocuments.slice(0, COLLAPSED_DOCUMENTS_PER_TYPE_CAP)
    const remainingCount =
      section.eltDocuments.length +
      section.qbrDocuments.length -
      visibleEltDocuments.length -
      visibleQbrDocuments.length

    return (
      <div className={styles.root}>
        {(canCreate || section.eltDocuments.length > 0) && (
          <div className={styles.typeGroup}>
            {canCreate ? (
              <EltCreateControls partnerId={section.partnerId} documents={section.eltDocuments} />
            ) : (
              <CockpitSectionHeader>
                <Translation span text={KEYS.sectionElt} /> — FY{fYear} Q{fQuarter}
              </CockpitSectionHeader>
            )}
            {visibleEltDocuments.map((document) => {
              const canDownload = canDownloadEltPptx(document)

              return (
                <div key={document.EntryId} className={styles.documentRow}>
                  <span className={styles.documentLabel}>
                    {getEltDocumentDisplayName(document)} — FY{document.FYear} Q{document.FQuarter}
                  </span>
                  <span className={styles.actions}>
                    <Button
                      variant="text"
                      size="sm"
                      href={buildEltDocumentOpenLink(
                        section.partnerId,
                        document.FYear,
                        document.UID
                      )}
                      target="_blank"
                    >
                      <Translation span text={KEYS.actions.open} />
                    </Button>
                    <Tooltip
                      disabled={canDownload}
                      tipContent={() => (
                        <Translation span text={KEYS.actions.downloadDisabledTooltip} />
                      )}
                    >
                      <span className={styles.disabledActionTrigger}>
                        <Button
                          variant="text"
                          size="sm"
                          iconButton="kbase kbase-download"
                          disabled={!canDownload}
                          onClick={() => downloadEltDocumentPptx(document)}
                          title={canDownload ? KEYS.actions.download : undefined}
                        />
                      </span>
                    </Tooltip>
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {(canCreate || section.qbrDocuments.length > 0) && (
          <div className={styles.typeGroup}>
            {canCreate ? (
              <QbrCreateControls partnerId={section.partnerId} rtm={section.rtm} />
            ) : (
              <CockpitSectionHeader>
                <Translation span text={KEYS.sectionQbr} />
              </CockpitSectionHeader>
            )}
            {visibleQbrDocuments.map((document) => {
              const canDownload = canDownloadQbrPptx(document)

              return (
                <div key={document.EntryId} className={styles.documentRow}>
                  <span className={styles.documentLabel}>
                    {getQbrDocumentDisplayName(document)} — FY{document.FYear ?? '—'} Q
                    {document.FQuarter ?? '—'}
                  </span>
                  <span className={styles.actions}>
                    <Button
                      variant="text"
                      size="sm"
                      href={buildQbrDocumentOpenLink(document.EntryId)}
                      target="_blank"
                    >
                      <Translation span text={KEYS.actions.open} />
                    </Button>
                    <Tooltip
                      disabled={canDownload}
                      tipContent={() => (
                        <Translation span text={KEYS.actions.downloadDisabledTooltip} />
                      )}
                    >
                      <span className={styles.disabledActionTrigger}>
                        <Button
                          variant="text"
                          size="sm"
                          iconButton="kbase kbase-download"
                          disabled={!canDownload}
                          onClick={() => downloadQbrDocumentPptx(document)}
                          title={canDownload ? KEYS.actions.download : undefined}
                        />
                      </span>
                    </Tooltip>
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {onExpand && remainingCount > 0 && (
          <CockpitExpandButton className={styles.expandButton} onClick={onExpand}>
            <Translation
              span
              text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.pendingActions.expandMore}
              replaceParams={[{ key: '{count}', value: String(remainingCount) }]}
            />
          </CockpitExpandButton>
        )}

        {exportPlan}
        {showLinks && (
          <div className={styles.typeGroup}>
            <CockpitSectionHeader>
              <Translation span text={KEYS.sectionLinks} />
            </CockpitSectionHeader>
            <CockpitDocumentLinks partnerId={section.partnerId} partnerRtm={section.rtm} />
          </div>
        )}
      </div>
    )
  }

  const visibleSections = sections.slice(0, COLLAPSED_PARTNER_CAP)
  const remainingCount = sections.length - visibleSections.length

  return (
    <div className={styles.root}>
      {visibleSections.map((section) => (
        <div key={section.partnerId} className={styles.partnerSummary}>
          <div className={styles.partnerName}>{section.partnerName}</div>
          <div className={styles.partnerCounts}>
            <Translation
              span
              text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks.countSummary}
              replaceParams={[
                { key: '{elt}', value: String(section.eltDocuments.length) },
                { key: '{qbr}', value: String(section.qbrDocuments.length) },
              ]}
            />
          </div>
        </div>
      ))}

      {onExpand && remainingCount > 0 && (
        <CockpitExpandButton className={styles.expandButton} onClick={onExpand}>
          <Translation
            span
            text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.pendingActions.expandMore}
            replaceParams={[{ key: '{count}', value: String(remainingCount) }]}
          />
        </CockpitExpandButton>
      )}
    </div>
  )
}

CockpitDocumentsCollapsedSummary.displayName = 'CockpitDocumentsCollapsedSummary'
